import { Link, useNavigate } from "react-router-dom"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@components/common/card"
import { DictType } from "@pma/types"
import { Button } from "@components/common/button"
import AssignToProject from "./assign-to-project"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import axios from "axios"
import { PROJECT_MEMBERS_REMOVE, SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import { useSearchParams } from "react-router-dom"
import { useEffect, useState } from "react"
import ProjectPermissions from "./project-permissions"
import { cn } from "@pma/utils/helpers"
import { PlusCircle } from "lucide-react"
import PmsLoader from "../common/loader"

interface Props {
  project: DictType
  className: string
}

const ProjectMembers = ({ project, className = "" }: Props) => {
  const navigate = useNavigate()
  const { translate, locale } = useTranslator()
  const [searchParam] = useSearchParams()

  const members = project?.members_id || []
  const isHebrew = locale === "he"
  const newParams = new URLSearchParams(searchParam)
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [open, setOpen] = useState<boolean>(false)
  const [userList, setUserList] = useState<{ user: DictType; isMember: boolean }[]>([])

  const removeUser = async (user_id: number) => {
    const response = await axios.post(
      SERVER_URL + PROJECT_MEMBERS_REMOVE,
      {
        project_id: project.id,
        user_id: user_id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const removeUserMutation = useMutation({
    mutationKey: ["remove-member"],
    mutationFn: removeUser,
    onSuccess: () => {
      newParams.set("revalidate", "projects")
      navigate(`?${newParams.toString()}`)
      refetch()
      setUserList([])
      toast({
        title: translate("Success"),
        description: translate("Member removed from project"),
        variant: "success",
      })
    },
    onError: () => {
      toast({
        title: translate("Error"),
        description: translate("Unable to remove member"),
        variant: "error",
      })
    },
  })

  const getProjectRoles = async () => {
    const response = await axios.get(SERVER_URL + `/projects/roles/get?project_id=${project.id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const {
    data: projectUserBaseOnRole,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["user-list", project?.id],
    queryFn: getProjectRoles,
    enabled: project?._can_change_access,
  })

  useEffect(() => {
    if (isFetching == false) {
      if (projectUserBaseOnRole?.length) {
        const userIdsInArray2 = new Set(members.map((item: any) => item.id))
        const transformed = projectUserBaseOnRole.map((item: any) => {
          const user = item?._user
          const { _user, ...restRole } = item
          return {
            id: user?.id,
            name: user?.name,
            user_picture: user?.user_picture,
            _role: restRole,
          }
        })

        const combined = [...members, ...transformed]

        const uniqueById = Object.values(
          combined.reduce(
            (acc, item) => {
              acc[item.id] = item // overwrite if duplicate id
              return acc
            },
            {} as Record<number, (typeof members)[0]>,
          ),
        )

        const result = uniqueById.map((item: any) => {
          return {
            user: { ...item },
            isMember: userIdsInArray2.has(item.id),
            flag: userIdsInArray2.has(item.id) ? 1 : 0, // 0 - Add, 1 - update
          }
        })
        setUserList(result)
      } else {
        const result = members?.map((item: any) => ({ user: item, isMember: true, flag: 0 })) // 0 - Add, 1 - update
        setUserList(result)
      }
    }
  }, [isFetching])

  const handleClose = () => {
    refetch()
    setUserList([])
    setOpen(false)
  }

  return project?._can_change_access ? (
    <div>
      <Card className='my-6 space-y-3 border-none shadow-none'>
        <CardHeader className={cn("", isHebrew ? "rtl" : "ltr")}>
          {project?._can_change_access && (
            <div className='flex !justify-end'>
              <Button
                variant={"ghost"}
                className='flex w-fit gap-2 text-xs'
                onClick={() => setOpen(true)}
                disabled={open}
              >
                {translate("Manage Members Role")} <PlusCircle className='text-primary' size={22} />
              </Button>
            </div>
          )}
        </CardHeader>
        {isLoading || isFetching ? (
          <div className='m-auto flex items-center justify-center'>
            <PmsLoader label='Loading' />
          </div>
        ) : (
          <CardContent
            className={cn(
              "grid max-h-[70vh] gap-4 overflow-auto p-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4",
              className,
            )}
          >
            {userList?.map((member: DictType) => (
              <Card
                key={member?.id}
                className={cn("relative overflow-hidden", isHebrew ? "rtl" : "ltr")}
              >
                {/* Ribbon */}
                {member?.isMember && (
                  <div
                    className={cn(
                      "absolute top-4 z-10 w-[150px] bg-primary py-1 text-center text-[10px] font-semibold uppercase text-white shadow-md",
                      isHebrew ? "right-[-45px] rotate-45" : "left-[-45px] -rotate-45",
                    )}
                  >
                    {translate("Member")}
                  </div>
                )}

                <CardContent className='flex flex-col items-center space-x-2 space-y-2 p-2'>
                  <img
                    alt=''
                    src={`${member?.user?.user_picture?.url || process.env.PUBLIC_URL + "/user-profile.svg"}`}
                    className='h-[50px] w-[50px] rounded-full object-cover xl:h-[80px] xl:w-[80px]'
                  />
                  <div className='flex flex-col'>
                    <span className='max-w-[500px] truncate font-medium capitalize'>
                      {member?.user?.name}
                    </span>
                  </div>
                </CardContent>
                <CardFooter className='mt-2 flex w-full flex-col items-center justify-center gap-3'>
                  <div className='flex w-full flex-row justify-center gap-5'>
                    {project?._can_change_access && (
                      <Button
                        variant={"destructive"}
                        size={"sm"}
                        loading={removeUserMutation.isPending}
                        disabled={removeUserMutation.isPending}
                        onClick={() => removeUserMutation.mutate(member?.user?.id)}
                      >
                        {translate("Remove")}
                      </Button>
                    )}

                    <Link to={USERS_PAGE_ROUTE + `/${member?.user?.id}`}>
                      <Button
                        variant={"outline"}
                        size={"sm"}
                        disabled={removeUserMutation.isPending}
                      >
                        {translate("View Profile")}
                      </Button>
                    </Link>
                  </div>
                  {project?._can_change_access && (
                    <ProjectPermissions
                      setUserList={setUserList}
                      projectId={project.id}
                      userId={member?.user?.id}
                      memberAccess={member?.user?._role || null}
                    />
                  )}
                </CardFooter>
              </Card>
            ))}
          </CardContent>
        )}
      </Card>

      {open && (
        <AssignToProject project={project} list={userList} open={open} handleClose={handleClose} />
      )}
    </div>
  ) : (
    <div className='m-auto flex items-center justify-center'>
      {translate("You have not access")}
    </div>
  )
}

export default ProjectMembers
